import React, { useEffect, useState } from 'react';
import './Admin_dashboard.css'; // Shared CSS for general layout and modal
import { useNavigate } from 'react-router-dom';

export default function AdminDashboard({ setLoggedIn }) { // Renamed component
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null); // Product being updated
  const [updateFiles, setUpdateFiles] = useState(null); // New state for files in update modal
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Check session and fetch products on component mount
    fetch('/backend/check_session.php', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.authenticated == false) {
          localStorage.removeItem("loggedIn");
          navigate('/formeb-login');
        } else {
          setUser(d.username || 'Admin');
          fetchProducts(); // Fetch products after successful authentication
        }
      });
  }, [navigate]);

  // Function to fetch products from the backend
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch('/backend/get_products.php', { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setProducts(data.products);
      } else {
        setMessage(data.message || 'Failed to fetch products.');
      }
    } catch (err) {
      setMessage('Server error: Could not fetch products.');
    } finally {
      setLoading(false);
    }
  };

  // Handles deletion of a product
  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const fd = new FormData();
      fd.append('product_id', productId);
      const res = await fetch('/backend/delete_product.php', {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Product deleted successfully!');
        fetchProducts(); // Refresh the list
      } else {
        setMessage(data.message || 'Failed to delete product.');
      }
    } catch (err) {
      setMessage('Server error: Could not delete product.');
    } finally {
      setLoading(false);
    }
  };

  // Opens the update modal and sets the product to be updated
  const handleUpdateClick = (product) => {
    setCurrentProduct(product);
    setUpdateFiles(null); // Clear any previously selected files for update
    setShowUpdateModal(true);
  };

  // Closes the update modal
  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setCurrentProduct(null);
    setUpdateFiles(null); // Clear files on close
  };

  const handleViewMedia = (product) => {
  setSelectedMedia(product.media || []);
  setShowMediaModal(true);
  };

  const handleCloseMediaModal = () => {
    setShowMediaModal(false);
    setSelectedMedia([]);
  };

  // Handles submission of the update form
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    const fd = new FormData();
    // Append all text fields from currentProduct to FormData
    for (const key in currentProduct) {
      // Exclude 'media_files' or any other non-form field properties if they exist on currentProduct
      if (currentProduct.hasOwnProperty(key) && key !== 'media_files') { 
        fd.append(key, currentProduct[key]);
      }
    }
    
    // Append new media files if selected
    if (updateFiles) {
      for (let i = 0; i < updateFiles.length; i++) {
        fd.append('media[]', updateFiles[i]);
      }
    }

    try {
      const res = await fetch('/backend/update_product.php', {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Product updated successfully!');
        handleCloseUpdateModal(); // Close modal on success
        fetchProducts(); // Refresh the list
      } else {
        setMessage(data.message || 'Failed to update product.');
      }
    } catch (err) {
      setMessage('Server error: Could not update product.');
    } finally {
      setLoading(false);
    }
  };

  // Handles changes in the update modal form's text inputs
  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({ ...prev, [name]: value }));
  };

  // Handles changes in the update modal form's file input
  const handleUpdateFiles = (e) => {
    setUpdateFiles(e.target.files);
  };

  // Handles admin logout
  async function logout() {
    await fetch('/backend/logout.php', { credentials: 'include' });
    if (typeof setLoggedIn === 'function') setLoggedIn(false);
    localStorage.removeItem("loggedIn");
    navigate('/formeb-login');
  }

  // Handles navigation to the Add Product page
  const handleAddProductClick = () => {
    navigate('/formeb-dashboard/add-product'); // Assuming your route for AddProduct is /admin/add-product
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h2>Admin Dashboard</h2>
        
        <div>
          {/* Button to navigate to Add Product page */}
          <button className="btn-secondary" onClick={handleAddProductClick}>Add New Product</button>
          <span>    </span>
          <button className="btn-logout" onClick={logout}>Logout</button>
        </div>
      </div>

      <h4>Hello {user}</h4>

      <div className="product-list-section">
        <h3>Product List</h3>
        {message && <div className="message">{message}</div>}
        {loading && products.length === 0 ? (
          <div className="loading-spinner-container">
            <div className="spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : products.length > 0 ? (
          <div className="product-table-container">
            <table>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Name (ID)</th>
                  <th>Category (EN)</th>
                  <th>Price</th>
                  <th>Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product.id}>
                    <td>{product.no}</td>
                    <td>{product.name_id}</td>
                    <td>{product.category}</td>
                    <td>Rp {parseFloat(product.price).toFixed(2)}</td>
                    <td>{product.location}</td>
                    <td>
                      <button className="btn-media" onClick={() => handleViewMedia(product)}>View Media</button>
                      <button className="btn-update" onClick={() => handleUpdateClick(product)}>Update</button>
                      <button className="btn-delete" onClick={() => handleDelete(product.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No products found.</p>
        )}
      </div>

      {/* Update Product Modal */}
      {showUpdateModal && currentProduct && (
        <div className="loading-modal"> {/* Reusing loading-modal for overlay */}
          <div className="update-modal-content">
            <h3>Update Product</h3>
            <form onSubmit={handleUpdateSubmit}>
              <input name="name_id" placeholder="Product name (Indonesia)" value={currentProduct.name_id || ''} onChange={handleUpdateChange} required />
              <input name="name_en" placeholder="Product name (English)" value={currentProduct.name || ''} onChange={handleUpdateChange} required />
              <input name="name_cn" placeholder="Product name (Chinese)" value={currentProduct.name_cn || ''} onChange={handleUpdateChange} required />
              <textarea name="description_id" placeholder="Description (Indonesia)" value={currentProduct.description_id || ''} onChange={handleUpdateChange} />
              <textarea name="description_en" placeholder="Description (English)" value={currentProduct.description || ''} onChange={handleUpdateChange} />
              <textarea name="description_cn" placeholder="Description (Chinese)" value={currentProduct.description_cn || ''} onChange={handleUpdateChange} />
              <input name="category_id" placeholder="Category (Indonesia)" value={currentProduct.category_id || ''} onChange={handleUpdateChange} />
              <input name="category_en" placeholder="Category (English)" value={currentProduct.category || ''} onChange={handleUpdateChange} />
              <input name="category_cn" placeholder="Category (Chinese)" value={currentProduct.category_cn || ''} onChange={handleUpdateChange} />
              <input name="price" placeholder="Price" type="number" step="0.01" value={currentProduct.price || ''} onChange={handleUpdateChange} />
              <input name="location" placeholder="Location" value={currentProduct.location || ''} onChange={handleUpdateChange} />
              <input type="file" name="media" multiple onChange={handleUpdateFiles} accept="image/*,video/*" /> {/* File input for media */}
              <div className="modal-actions">
                <button type="submit" disabled={loading}>Save Changes</button>
                <button type="button" onClick={handleCloseUpdateModal} disabled={loading}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Modal */}
      {showMediaModal && (
        <div className="loading-modal">
          <div className="update-modal-content media-modal">
            <h3>Product Media</h3>

            {selectedMedia.length > 0 ? (
              <div className="media-grid">
                {selectedMedia.map((item, index) => (
                  <div key={index} className="media-item">
                    {item.type === "image" ? (
                      <img
                        src={item.src}
                        alt={`media-${index}`}
                        className="media-preview"
                      />
                    ) : (
                      <video
                        controls
                        className="media-preview"
                      >
                        <source
                          src={item.src}
                          type="video/mp4"
                        />
                        Your browser does not support video.
                      </video>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p>No media found.</p>
            )}

            <div className="modal-actions">
              <button onClick={handleCloseMediaModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Loading Overlay (for delete/update operations) */}
      {loading && showUpdateModal === false && ( // Only show global loading if not showing update modal
        <div className="loading-modal">
          <div className="loading-content">
            <div className="spinner"></div>
            <p>Processing request...</p>
          </div>
        </div>
      )}
    </div>
  );
}
